import { TFunction } from 'i18next';
import {
  BookingType,
  LatestEventType,
  ParkingType,
  Subscription,
  UserType,
} from '../../../../utils/utilTypes';
import { BookingWithGateDescription } from '../../../../utils/utilsBooking';
import { isSubscriptionCase, isOrgAccessRestricted } from '../../../../utils/utilsParking';
import { isUserParkingRequestApproved } from '../../../../utils/utilsUser';

export const SHOW_GATES_OPEN_TIMEOUT_IN_MS = 15_000;

const possibleTypes = (t: TFunction) => {
  return {
    selectedParking: () => ({
      type: 'selectedParking',
      title: t('selectedParkingTitle'),
      description: t('selectedParkingDescription', {
        radius: process.env.NEXT_PUBLIC_GEOFENCE_RADIUS_IN_METERS,
      }),
    }),
    withinParkingRadius: () => ({
      type: 'withinParkingRadius',
      title: t('withinParkingRadiusTitle'),
      description: t('withinParkingRadiusDescription'),
    }),
    unlockingSelectedParking: () => ({
      type: 'unlockingSelectedParking',
      title: t('unlockingSelectedParkingTitle'),
      description: t('unlockingSelectedParkingDescription'),
    }),
    // gatesAreOpen: () => ({
    //   type: 'gatesAreOpen',
    //   title: t('gatesAreOpenTitle'),
    //   description: t('gatesAreOpenDescription'),
    // }),
    unavailable: () => ({
      type: 'unavailable',
      title: t('unavailableTitle'),
      description: t('unavailableDescription'),
    }),
    subscription: () => ({
      type: 'subscription',
      title: t('subscriptionTitle'),
      description: t('subscriptionDescription'),
    }),
    requiresApproval: () => ({
      type: 'requiresApproval',
      title: t('requiresApprovalTitle'),
      description: t('requiresApprovalDescription'),
    }),
    accessRestricted: () => ({
      type: 'accessRestricted',
      title: t('accessRestrictedTitle'),
      description: t('accessRestrictedDescription'),
    }),

    upcomingBookings: (bookings: BookingWithGateDescription[]) => ({
      type: 'gatesAreBooked',
      title: t('gatesAreBookedTitle'),
      description: `
        <div class="flex flex-col">
          <ul class="list-disc max-h-[150px] overflow-y-auto">
            ${bookings
              .sort(
                (a, b) =>
                  new Date(a.startsAt).getTime() -
                  new Date(b.startsAt).getTime(),
              )
              .map(
                (booking) => `
            <li class="text-xs">${t('gatesAreBookedDescription', {
              gateAlias: booking.gateDescription || 'Gate Description not set',
              bookingDateStart: new Date(booking.startsAt).toLocaleString(
                'en-GB',
                {
                  timeStyle: 'short',
                  dateStyle: 'short',
                  timeZone: 'Europe/Bucharest',
                },
              ),
              bookingDateEnd: new Date(booking.endsAt).toLocaleString('en-GB', {
                timeStyle: 'short',
                dateStyle: 'short',
                timeZone: 'Europe/Bucharest',
              }),
            })}</li>
            `,
              )
              .join('')}
          </ul>
          <div class="text-sm">${t('gatesAreBookedNote')}</div>
        </div>
      `,
    }),
  };
};

export const inferParkingModal = (
  currentUser: UserType | null,
  t: TFunction,
  userSubscriptions: Subscription[],
  latestEvent: LatestEventType | null,
  otherBookings: BookingWithGateDescription[],
  parking?: ParkingType,
) => {
  if (parking?.isUnavailable) {
    return possibleTypes(t).unavailable();
  }

  if (parking && isOrgAccessRestricted(currentUser, parking)) {
    return possibleTypes(t).accessRestricted();
  }

  if (
    parking?.requiresApproval &&
    !isUserParkingRequestApproved(currentUser, parking)
  ) {
    return possibleTypes(t).requiresApproval();
  }

  if (parking && isSubscriptionCase(currentUser, parking, userSubscriptions)) {
    return possibleTypes(t).subscription();
  }

  if (
    latestEvent?.status === 'sim_pending' &&
    latestEvent?.twilioCallResponse === false
  ) {
    return possibleTypes(t).unlockingSelectedParking();
  }

  if (otherBookings.length > 0 && parking?.isSelected) {
    return possibleTypes(t).upcomingBookings(otherBookings);
  }

  if (parking?.isInUnlockRadius && parking?.isSelected) {
    return possibleTypes(t).withinParkingRadius();
  }

  if (parking?.isSelected) {
    return possibleTypes(t).selectedParking();
  }

  return null;
};
